import { Button } from 'antd';
import { CloseOutlined, ExportOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../app/store';
import { closeSelectedPanel, SelectedFeature } from '../../app/chatSlice';
import './SelectedFeaturePanel.css';

const formatRange = (f: SelectedFeature) => `${f.chromosome}:${f.start.toLocaleString()}-${f.end.toLocaleString()}`;

const HIDDEN_ATTR_KEYS = new Set(['id', 'parent', 'name']);

const FeatureBlock = ({ feature, heading }: { feature: SelectedFeature; heading: string }) => {
    const facts: [string, string][] = [];

    Object.entries(feature.attributes).forEach(([k, v]) => {
        if (HIDDEN_ATTR_KEYS.has(k.toLowerCase())) return;
        facts.push([k, v]);
    });

    return (
        <div className="F-SelectedFeature__block">
            <div className="F-SelectedFeature__section-title">{heading}</div>
            <div className="F-SelectedFeature__id">{feature.id}</div>
            <dl className="F-SelectedFeature__dl">
                <dt>Type</dt>
                <dd>{feature.type}</dd>
                <dt>Location</dt>
                <dd>{formatRange(feature)}</dd>
                {feature.strand && (
                    <>
                        <dt>Strand</dt>
                        <dd>{feature.strand}</dd>
                    </>
                )}
                <dt>Length</dt>
                <dd>{(feature.end - feature.start + 1).toLocaleString()} bp</dd>
                {facts.map(([k, v]) => (
                    <div key={k} className="F-SelectedFeature__dl-row">
                        <dt>{k}</dt>
                        <dd>{v}</dd>
                    </div>
                ))}
            </dl>
        </div>
    );
};

// Use the exact symbol displayed in the panel header (`feature.id`) so the
// GeneCards link always matches what the user sees as the gene name.
const geneCardsUrl = (gene: SelectedFeature): string =>
    `https://www.genecards.org/cgi-bin/carddisp.pl?gene=${encodeURIComponent(gene.id)}`;

export const SelectedFeaturePanel = () => {
    const dispatch = useDispatch();
    const feature = useSelector((s: RootState) => s.chat.selectedFeature);
    const isOpen = useSelector((s: RootState) => s.chat.isSelectedPanelOpen);

    if (!isOpen || !feature) return null;

    // Walk parent links to render full ancestry top -> selected (e.g. gene -> mRNA -> exon).
    const chain: SelectedFeature[] = [];
    {
        let node: SelectedFeature | undefined = feature;
        const guard = new Set<string>();
        while (node && chain.length < 8 && !guard.has(node.id)) {
            guard.add(node.id);
            chain.unshift(node);
            node = node.parent;
        }
    }

    const headingFor = (f: SelectedFeature, isSelected: boolean): string => {
        const t = f.type.toLowerCase();
        if (t === 'gene') return 'Gene';
        if (t === 'mrna') return 'Transcript (mRNA)';
        if (t === 'lncrna') return 'Transcript (lncRNA)';
        if (t === 'transcript') return 'Transcript';
        const cap = f.type.charAt(0).toUpperCase() + f.type.slice(1);
        return isSelected ? `Selected ${cap.toLowerCase()}` : cap;
    };

    const gene = chain.find((f) => f.type.toLowerCase() === 'gene');

    return (
        <div className="F-SelectedFeature">
            <div className="F-SelectedFeature__header">
                <span className="F-SelectedFeature__header-title">Selected element</span>
                <Button
                    type="text"
                    size="small"
                    icon={<CloseOutlined />}
                    onClick={() => dispatch(closeSelectedPanel())}
                    aria-label="Close selected element panel"
                />
            </div>
            {chain.map((node, idx) => (
                <FeatureBlock
                    key={node.id || `${node.type}-${idx}`}
                    feature={node}
                    heading={headingFor(node, idx === chain.length - 1)}
                />
            ))}
            {gene && (
                <div className="F-SelectedFeature__links">
                    <a
                        href={geneCardsUrl(gene)}
                        target="_blank"
                        rel="noreferrer"
                        className="F-SelectedFeature__link"
                    >
                        Show in GeneCards <ExportOutlined />
                    </a>
                </div>
            )}
        </div>
    );
};
